import { FC, PropsWithChildren } from 'react';
import { Overlay } from 'ui/overlay';
import { NavBar as DefaultNavBar } from './NavBar';
import { PanelHeader, PanelFooter } from './Panel';
import { TakeOver } from './TakeOver';
import { TakeOverProps, PanelSize } from './TakeOver.types';

export interface FullScreenTakeOverProps extends TakeOverProps {
  /** Whether takeover should have default shaded overlay. Pass in `null` to override the default overlay */
  Overlay?: React.ReactNode;
  /** Title of the TakeOver. Will be used in default NavBar if NavBar component is not provided. */
  title?: string;
  /** One of the pre-defined sizes for the Full Screen TakeOver component */
  size?: PanelSize.Large | PanelSize.Xlarge | PanelSize.FullWidth;
}

/**
 The Full Screen TakeOver component is a larger variant of the TakeOver component that can be used in the following scenarios:
    - FullWidth (full-page Take Over)
    - Xlarge (almost full-page, for complex interactions or forms inside of a larger more complex flow)
    - Large (for complex interactions or forms inside of a larger more complex flow)
 */
export const FullScreenTakeOver: FC<PropsWithChildren<FullScreenTakeOverProps>> = ({
  withPortal = true,
  size = PanelSize.Xlarge,
  Header,
  Footer,
  NavBar,
  ...props
}): JSX.Element => {
  // Use the default components if one is not provided
  const NavCmp = NavBar || <DefaultNavBar title={props.title} onBackClick={props.onClose} />;
  const OverlayCmp = <Overlay onOverlayClick={props.onClose} zIndex={-1} />;
  const HeaderCmp = Header ? <PanelHeader>{Header}</PanelHeader> : null;
  const FooterCmp = Footer ? <PanelFooter>{Footer}</PanelFooter> : null;

  return (
    <TakeOver
      size={size}
      NavBar={NavCmp}
      Overlay={OverlayCmp}
      Header={HeaderCmp}
      Footer={FooterCmp}
      withPortal={withPortal}
      {...props}
    />
  );
};
